import * as React from 'react'
import { Icon, TabbedContent } from 'src/design-system'
import styles from './TabbedCard.module.scss'
import {
  ChevronUp,
  ChevronDown,
  CloseOutline,
  MessageSquareOutline,
} from '@easy-eva-icons/react'
import Select from 'react-select'
import { Team } from '../types'
import { DotsSixVertical } from '@phosphor-icons/react'

export type TabbedCardProps = {
  collapsed: boolean
  draggable?: boolean
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>
  onAnchorClick?: () => void
  onClose?: () => void
  onTeamSelect: (id: number) => void
  onToggleCollapsed?: () => void
  selectedTeam: number | null
  style?: React.CSSProperties
  tabs: [React.ReactNode, React.ReactNode][]
  teams?: (Team & { selected: boolean })[]
  title: React.ReactNode
}

// eslint-disable-next-line react/display-name
export const TabbedCard = React.forwardRef<HTMLElement, TabbedCardProps>(
  (
    {
      collapsed,
      draggable = false,
      dragHandleProps,
      onAnchorClick,
      onToggleCollapsed,
      onClose,
      onTeamSelect,
      selectedTeam,
      style,
      tabs,
      teams = [],
      title,
    },
    ref
  ) => {
    const teamsForSelect = teams.map((t) => ({
      value: t.id,
      label: t.name,
      selected: t.selected,
    }))
    return (
      <article ref={ref}>
        <TabbedContent.Root
          className={styles.container}
          initialTabId="0"
          style={style}
        >
          <header
            className={styles.header}
            style={{ marginBottom: collapsed ? '0' : '1rem' }}
          >
            <div style={{ alignItems: 'center', display: 'flex' }}>
              {draggable && (
                <button className={styles.dndHandle} {...dragHandleProps}>
                  <DotsSixVertical
                    color="#bec4d5"
                    height="1.25rem"
                    width="1.25rem"
                  />
                </button>
              )}
              {/* TODO: the title shifts slightly when collapsing/expanding */}
              <h3 className={styles.title}>{title}</h3>
            </div>
            <div className={styles.buttonGroup}>
              {!collapsed && teams && (
                <Select
                  isClearable
                  onChange={(selection) => onTeamSelect(selection?.value || 0)}
                  options={teamsForSelect}
                  styles={{
                    indicatorSeparator: (_baseStyles) => ({ display: 'none' }),
                  }}
                  value={teamsForSelect.find(
                    (team) => team.value === selectedTeam
                  )}
                />
              )}
              {onAnchorClick && (
                <button
                  aria-label="Go to message"
                  onClick={() => onAnchorClick()}
                >
                  <Icon size="sm">
                    <MessageSquareOutline />
                  </Icon>
                </button>
              )}
              {onClose && (
                <button aria-label="Close" onClick={() => onClose()}>
                  <Icon size="sm">
                    <CloseOutline />
                  </Icon>
                </button>
              )}
              {onToggleCollapsed && (
                <button
                  aria-label="Collapse"
                  onClick={() => onToggleCollapsed()}
                >
                  <Icon size="sm">
                    {collapsed ? <ChevronDown /> : <ChevronUp />}
                  </Icon>
                </button>
              )}
            </div>
            {!collapsed && (
              <TabbedContent.Tabs variant="underlineSlim">
                {tabs.map(([label], i) => (
                  <TabbedContent.Tab id={i.toString()} key={i.toString()}>
                    {label}
                  </TabbedContent.Tab>
                ))}
              </TabbedContent.Tabs>
            )}
          </header>
          {!collapsed &&
            tabs.map(([_label, content], i) => (
              <TabbedContent.Content
                className={styles.content}
                key={i.toString()}
                tabId={i.toString()}
              >
                {content}
              </TabbedContent.Content>
            ))}
        </TabbedContent.Root>
      </article>
    )
  }
)
